import { displayTitle, type KeyPointSet, type QualityItem, type QualityRun } from '@buakei/types';
import { Badge, Button, ErrorState, LoadingState } from '@buakei/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import type { IdentityClient } from '@/auth/identity-client';
import { qualityApi } from '@/services/api';

/**
 * Lawyer-approved key points (ADR-035): drafted from an answer every grader marked correct,
 * approved by a second lawyer, then given to the model for matching questions to cover in its
 * own words. Readers never see the points themselves.
 */

export const keyPointKeys = { list: ['quality', 'keypoints'] as const };

const POINT_LIMIT = 8;
const STATUS_LABEL: Record<KeyPointSet['status'], string> = {
  draft: 'Draft: awaiting approval',
  approved: 'Approved: guiding answers',
  retired: 'Retired',
};

/** Every grade says correct (or correctly declined), and at least one says correct. */
export function gradedCorrect(item: QualityItem): boolean {
  return (
    item.grades.some((g) => g.verdict === 'correct') &&
    item.grades.every((g) => g.verdict === 'correct' || g.verdict === 'correctly_declined')
  );
}

export function useKeyPoints(tokens: IdentityClient | null) {
  return useQuery({
    queryKey: keyPointKeys.list,
    queryFn: () => qualityApi.keyPoints(tokens!),
    enabled: tokens !== null,
  });
}

type DraftPoint = { text: string; sources: number[] };

/** The answer's cited statements, one point each, as a starting draft. */
function startingPoints(item: QualityItem): DraftPoint[] {
  return (item.answer?.segments ?? [])
    .filter((s) => s.sources.length > 0 && s.text.trim().length >= 5)
    .map((s) => ({ text: s.text.trim().replace(/^- /, ''), sources: s.sources.slice(0, 4) }))
    .slice(0, POINT_LIMIT);
}

/** Draft key points from one graded answer of a run. */
export function DraftKeyPoints({
  tokens,
  run,
  item,
}: {
  tokens: IdentityClient;
  run: QualityRun;
  item: QualityItem;
}) {
  const queryClient = useQueryClient();
  const [points, setPoints] = useState<DraftPoint[] | null>(null);
  const baseId = useId();
  const save = useMutation({
    mutationFn: () =>
      qualityApi.draftKeyPoints(tokens, {
        runId: run.id,
        questionId: item.question.id,
        points: points!.map((p) => ({ text: p.text.trim(), sources: p.sources })),
      }),
    onSuccess: () => {
      setPoints(null);
      void queryClient.invalidateQueries({ queryKey: keyPointKeys.list });
    },
  });
  const sources = item.answer?.sources ?? [];

  if (!points) {
    return (
      <div className="flex flex-col gap-2">
        <Button
          variant="secondary"
          size="sm"
          className="self-start"
          onClick={() => {
            save.reset();
            setPoints(startingPoints(item));
          }}
        >
          Draft key points from this answer
        </Button>
        {save.isSuccess ? (
          <p role="status" className="text-sm text-ink-900">
            Key points drafted. A different legal reviewer must approve them before they guide
            answers.
          </p>
        ) : null}
      </div>
    );
  }

  const update = (i: number, change: Partial<DraftPoint>) =>
    setPoints(points.map((p, j) => (j === i ? { ...p, ...change } : p)));
  const valid =
    points.length > 0 && points.every((p) => p.text.trim().length >= 5 && p.sources.length > 0);
  return (
    <form
      aria-label="Draft key points"
      className="flex flex-col gap-4 rounded-lg border border-line-200 bg-soft-white p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) save.mutate();
      }}
    >
      <div className="flex flex-col gap-1">
        <h4 className="text-sm font-semibold text-navy-900">Draft key points</h4>
        <p className="text-sm text-muted-600">
          State each point a good answer must make, and tick the sources it rests on. Matching
          questions will be researched with these points to cover, in the model's own words; readers
          never see them as written.
        </p>
      </div>
      <ol className="flex flex-col gap-4">
        {points.map((p, i) => (
          <li key={i} className="flex flex-col gap-2">
            <label htmlFor={`${baseId}-${i}`} className="text-sm font-medium text-ink-900">
              Point {i + 1}
            </label>
            <textarea
              id={`${baseId}-${i}`}
              rows={2}
              maxLength={600}
              value={p.text}
              onChange={(e) => update(i, { text: e.target.value })}
              className="rounded-md border border-line-200 bg-white p-2 text-ink-900"
            />
            <fieldset className="flex flex-col gap-1">
              <legend className="text-xs text-muted-600">Rests on (up to 4)</legend>
              {sources.map((s) => {
                const checked = p.sources.includes(s.n);
                return (
                  <label key={s.n} className="flex items-start gap-2 text-sm text-ink-900">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={checked}
                      disabled={!checked && p.sources.length >= 4}
                      onChange={() =>
                        update(i, {
                          sources: checked
                            ? p.sources.filter((n) => n !== s.n)
                            : [...p.sources, s.n].sort((a, b) => a - b),
                        })
                      }
                    />
                    <span>
                      [{s.n}] {displayTitle(s.title)} — {s.label}
                    </span>
                  </label>
                );
              })}
            </fieldset>
            <Button
              type="button"
              variant="link"
              size="sm"
              className="self-start"
              onClick={() => setPoints(points.filter((_, j) => j !== i))}
            >
              Remove point {i + 1}
            </Button>
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap gap-3">
        {points.length < POINT_LIMIT ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setPoints([...points, { text: '', sources: [] }])}
          >
            Add a point
          </Button>
        ) : null}
        <Button type="submit" size="sm" disabled={!valid || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save draft'}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => setPoints(null)}>
          Cancel
        </Button>
      </div>
      {!valid ? (
        <p className="text-sm text-muted-600">
          Each point needs at least five characters and one source.
        </p>
      ) : null}
      {save.isError ? (
        <p role="alert" className="text-sm text-ink-900">
          {save.error.message}
        </p>
      ) : null}
    </form>
  );
}

function KeyPointCard({
  tokens,
  set,
  canApprove,
  myUid,
}: {
  tokens: IdentityClient;
  set: KeyPointSet;
  canApprove: boolean;
  myUid: string;
}) {
  const queryClient = useQueryClient();
  const decide = useMutation({
    mutationFn: (decision: 'approve' | 'retire') =>
      qualityApi.decideKeyPoints(tokens, set.id, decision),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keyPointKeys.list }),
  });
  const own = set.createdByUid === myUid;
  return (
    <li className="flex flex-col gap-3 p-4">
      <span className="flex flex-wrap items-center gap-2 text-sm">
        <Badge tone={set.status === 'approved' ? 'success' : 'plain'}>
          {STATUS_LABEL[set.status]}
        </Badge>
        <span className="text-muted-600">
          Drafted by {set.createdByName ?? 'a lawyer'}
          {set.approvedByName ? ` · approved by ${set.approvedByName}` : ''}
        </span>
      </span>
      <p className="font-medium text-navy-900">{set.question}</p>
      <ol className="flex list-decimal flex-col gap-2 pl-5 text-sm text-ink-900">
        {set.points.map((p, i) => (
          <li key={i}>
            {p.text}
            <span className="block text-xs text-muted-600">
              {p.passages.map((s) => `${displayTitle(s.title)} — ${s.label}`).join('; ')}
            </span>
          </li>
        ))}
      </ol>
      {canApprove && set.status !== 'retired' ? (
        <div className="flex flex-wrap items-center gap-3">
          {set.status === 'draft' ? (
            <Button
              size="sm"
              disabled={own || decide.isPending}
              onClick={() => decide.mutate('approve')}
            >
              Approve
            </Button>
          ) : null}
          <Button
            variant="secondary"
            size="sm"
            disabled={decide.isPending}
            onClick={() => decide.mutate('retire')}
          >
            Retire
          </Button>
          {own && set.status === 'draft' ? (
            <span className="text-sm text-muted-600">
              Another legal reviewer must approve points you drafted.
            </span>
          ) : null}
        </div>
      ) : null}
      {decide.isError ? (
        <p role="alert" className="text-sm text-ink-900">
          {decide.error.message}
        </p>
      ) : null}
    </li>
  );
}

/** All key points, newest first, with approve and retire for legal reviewers. */
export function KeyPointsSection({ tokens, myUid }: { tokens: IdentityClient; myUid: string }) {
  const list = useKeyPoints(tokens);
  return (
    <section aria-labelledby="keypoints-h" className="flex flex-col gap-3">
      <h2 id="keypoints-h" className="font-serif text-xl font-semibold text-navy-900">
        Key points
      </h2>
      <p className="text-sm text-muted-600">
        Points a lawyer approved for common questions. Matching questions are researched with these
        points to cover, still cited from the library and written in the model's own words. Draft
        them from an answer every grader marked correct, in a review run.
      </p>
      {list.isPending ? <LoadingState label="Loading key points" /> : null}
      {list.isError ? (
        <ErrorState title="Key points could not be loaded" onRetry={() => void list.refetch()}>
          {list.error.message}
        </ErrorState>
      ) : null}
      {list.data && list.data.sets.length === 0 ? (
        <p className="text-sm text-muted-600">No key points yet.</p>
      ) : null}
      {list.data && list.data.sets.length > 0 ? (
        <ul className="flex flex-col divide-y divide-line-200 rounded-lg border border-line-200 bg-white">
          {list.data.sets.map((set) => (
            <KeyPointCard
              key={set.id}
              tokens={tokens}
              set={set}
              canApprove={list.data.canApprove}
              myUid={myUid}
            />
          ))}
        </ul>
      ) : null}
    </section>
  );
}
